import type {
  DrawLayerDeleteDTO,
  DrawLayerFeatureUpdateDTO,
  DrawLayerResponseDTO,
  EventResultDTO,
  OperationDefaultDrawLayerUpdatedEventDTO,
  OperationDeleteDTO,
  OperationMemberRemoveData,
  OperationTrackerDetailsEventDTO,
  OperationUserDTO,
  TrackerDeleteDTO,
  UserClearLocationData,
  UserDetailsUpdateEventDTO,
} from '@nora-monorepo/nora-api-client';
import { EventPattern } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import type { NoraWebViewLoadMapEvent, NoraWebViewUpdateMapLocationEvent, NoraWebViewUpdateMapSources } from '@nora-monorepo/ui-shared';
import { NoraWebViewEventPattern, useSseStore, useWebViewStore } from '@nora-monorepo/ui-shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';

type EventHandler = (event: EventResultDTO) => Promise<void>;

/**
 * Manages event handling for the NoraMap, including setting up listeners for various event patterns.
 */
export class EventManager {
  private sseStore = useSseStore();

  private offlineMapSourcesUnsubscribe: (() => void) | null = null;

  private updateMapLocationUnsubscribe: (() => void) | null = null;

  private loadMapUnsubscribe: (() => void) | null = null;

  private sseEventUnsubscribes: Array<() => void> = [];

  /**
   * Creates an instance of EventManager.
   * @param map The NoraMap instance to manage events for.
   */
  constructor(private readonly map: NoraMap) {}

  /**
   * Sets up event listeners for the NoraMap.
   */
  setup(): void {
    this.cleanup();
    this.initializeSseEventListeners();
    this.initialiseWebViewEventListeners();
  }

  /**
   * A mapping of event patterns to their corresponding handler functions.
   * Add new event handlers here as needed, but keep the functions concise. Implementation logic should be handled in appropriate managers.
   */
  private eventMap = new Map<EventPattern, EventHandler>([
    [EventPattern.OperationUpdateMember, async (event) => {
      const data = event.eventData;
      const operationUserData = data as OperationUserDTO;

      this.map.state.track.handleUserLocationScopeUpdate(operationUserData);
    }],

    [EventPattern.OperationMemberRemove, async (event) => {
      const data = event.eventData;
      const operationRemoveMemberData = data as OperationMemberRemoveData;

      this.map.state.track.handleOperationRemoveMemberEvent(operationRemoveMemberData);
    }],

    [EventPattern.OperationDelete, async (event) => {
      const data = event.eventData;
      const deletedOperationData = data as OperationDeleteDTO;

      this.map.state.track.handleOperationDeleteEvent(deletedOperationData);
    }],

    [EventPattern.TrackerCreate, async (event) => {
      const data = event.eventData;
      const operationTrackerData = data as OperationTrackerDetailsEventDTO;

      this.map.state.track.setTrackerPropertiesForTrack(operationTrackerData);
    }],

    [EventPattern.TrackerUpdate, async (event) => {
      const data = event.eventData;
      const operationTrackerData = data as OperationTrackerDetailsEventDTO;

      this.map.state.track.setTrackerPropertiesForTrack(operationTrackerData);
    }],

    [EventPattern.TrackerDelete, async (event) => {
      const data = event.eventData;
      const operationTrackerData = data as TrackerDeleteDTO;

      this.map.state.track.removeTrack(operationTrackerData.id);
    }],

    [EventPattern.DrawLayerCreate, async (event: EventResultDTO) => {
      const data = event.eventData;
      const drawLayerData = data as DrawLayerResponseDTO;

      await this.map.state.draw.handleDrawLayerCreateEvent(drawLayerData);
    }],

    [EventPattern.DrawLayerDelete, async (event: EventResultDTO) => {
      const data = event.eventData;
      const drawLayerData = data as DrawLayerDeleteDTO;

      await this.map.state.draw.handleDrawLayerDeleteEvent(drawLayerData);
    }],

    [EventPattern.DrawLayerUpdateMetadata, async (event: EventResultDTO) => {
      const data = event.eventData;
      const drawLayerData = data as DrawLayerResponseDTO;

      await this.map.state.draw.handleDrawLayerUpdateEvent(drawLayerData);
    }],

    [EventPattern.DrawFeatureUpdate, async (event: EventResultDTO) => {
      const data = event.eventData as DrawLayerFeatureUpdateDTO;

      await this.map.state.draw.handleDrawLayerFeatureUpdateEvent(data);
    }],

    [EventPattern.OperationDefaultLayerUpdated, async (event: EventResultDTO) => {
      const data = event.eventData as OperationDefaultDrawLayerUpdatedEventDTO;

      await this.map.state.draw.handleOperationDefaultDrawLayerUpdatedEvent(data);
    }],

    [EventPattern.UserClearLocation, async (event: EventResultDTO) => {
      const data = event.eventData;
      const userData = data as UserClearLocationData;

      this.map.state.track.removeTrack(userData.userId);
    }],

    [EventPattern.UserDetailsUpdate, async (event: EventResultDTO) => {
      const data = event.eventData;
      const userDetailsUpdateData = data as UserDetailsUpdateEventDTO;

      this.map.state.track.setUserPropertiesForTrack(userDetailsUpdateData);
    }],
  ]);

  /**
   * Initializes event listeners for various event patterns.
   */
  private initializeSseEventListeners(): void {
    for (const [eventPattern, handler] of this.eventMap.entries()) {
      const unsubscribe = this.sseStore.addEventHandler(eventPattern, handler);

      this.sseEventUnsubscribes.push(unsubscribe);
    }
  }

  private initialiseWebViewEventListeners(): void {
    const webViewStore = useWebViewStore();

    this.offlineMapSourcesUnsubscribe = webViewStore.addNativeLayerEventListener(
      NoraWebViewEventPattern.WEBVIEW_UPDATE_MAP_SOURCES,
      (event: CustomEvent<NoraWebViewUpdateMapSources>) => {
        event.detail.data.mapSources.forEach((mapSource) => {
          this.map.layers.addOfflineMap(mapSource);
        });
      },
    );

    this.updateMapLocationUnsubscribe = webViewStore.addNativeLayerEventListener(
      NoraWebViewEventPattern.WEBVIEW_UPDATE_MAP_LOCATION,
      (event: CustomEvent<NoraWebViewUpdateMapLocationEvent>) => {
        const {
          latitude,
          longitude,
          zoom,
          producerId,
        } = event.detail.data;

        this.map.setCenter({ latitude, longitude, zoom });

        if (isNonEmptyString(producerId)) {
          this.map.ref.setFollowedProducerId(producerId);
        }
      },
    );

    this.loadMapUnsubscribe = webViewStore.addNativeLayerEventListener(
      NoraWebViewEventPattern.WEBVIEW_LOAD_MAP,
      (event: CustomEvent<NoraWebViewLoadMapEvent>) => {
        const { operationId } = event.detail.data;

        // The native "load map" event means "switch to exactly this operation"
        this.map.switchToOperation(operationId).catch((error) => {
          console.error('[EventManager] Error switching operation from load map event:', error);
        });
      },
    );
  }

  cleanup(): void {
    try {
      this.offlineMapSourcesUnsubscribe?.();
      this.offlineMapSourcesUnsubscribe = null;

      this.updateMapLocationUnsubscribe?.();
      this.updateMapLocationUnsubscribe = null;

      this.loadMapUnsubscribe?.();
      this.loadMapUnsubscribe = null;

      for (const unsubscribe of this.sseEventUnsubscribes) {
        unsubscribe();
      }
      this.sseEventUnsubscribes = [];
    }
    catch (error) {
      console.error('[EventManager] Error cleaning up event listeners:', error);
    }
  }
}
